/* WeQuote Dev Board — Completed / Live Features (renderCompleted, faithful
 * port). Left: analytics — Shipped / Quick wins / On-time % / Announced %,
 * by-dev donut, avg dev-days by effort tier vs estimate, scoped This month /
 * This year / All time. Right: Awaiting announcement + Announced & live, each
 * card carrying the announce workflow (Announce to Requesters, Publish to
 * Updates Page, WhatsApp Groups, Not needed) — date-stamp toggles at parity,
 * the agreed real integrations come post-parity. */
import React, { useState } from 'react'
import { PublishNoteModal } from './ReleaseNotes'
import { deleteReleaseNotesByCard } from '../core/api'
import { useDevBoard } from '../DevBoardProvider'
import * as LV from '../core/live'
import { completedByDev, pieSlices } from '../core/overview'
import type { Feature } from '../core/types'
import { DL, devColor } from '../core/constants'
import { fmtDate, elapsedLabel } from '../core/dates'
import { IcBan, IcCheck, IcMail, IcMegaphone, IcMessage } from '../icons'

export const CompletedView: React.FC = () => {
  const [publishFor, setPublishFor] = useState<Feature | null>(null)
  const { cards, mutate, openCardModal, showToast } = useDevBoard()
  /* Unpublish (Lee, 4 Sep): the Published stamp is linked to the actual
   * Release Notes entry — deselecting warns, then removes the entry too. */
  const unpublish = async (f: Feature) => {
    if (!window.confirm('You’re about to unpublish “' + f.title + '” — its entry will be removed from the Release Notes page (and the WeQuote embed). Are you sure?')) return
    try {
      const n = await deleteReleaseNotesByCard(f.id)
      mutate((fs) => LV.publishFeature(fs, f.id))
      if (n === 0) showToast('Unpublished — no Release Notes entry was linked to this card.')
    } catch (e: any) { showToast('Unpublish failed: ' + e.message) }
  }
  const [scope, setScope] = useState<LV.LiveScope>('month')
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  const allLive = cards.filter((f) => f.status === 'live')
  const scoped = allLive.filter((f) => LV.liveInScope(f, scope))
  const withDl = scoped.filter((f) => f.deadline && f.completedOn)
  const onTimePct = withDl.length ? Math.round((withDl.filter((f) => f.completedOn <= f.deadline).length / withDl.length) * 100) : null
  const qw = scoped.filter((f) => f.eff === 'quickwin').length
  const needAnn = scoped.filter((f) => !f.noAnnounce)
  const annPct = needAnn.length ? Math.round((needAnn.filter(LV.liveAnnounced).length / needAnn.length) * 100) : null
  const byDev = completedByDev(scoped)
  const pie = pieSlices(byDev)
  const legendKeys = Object.keys(byDev).sort((a, b) => byDev[b] - byDev[a])

  const effStat = (eff: string) => {
    const l = scoped.filter((f) => f.eff === eff && LV.actualDevDays(f) != null)
    if (!l.length) return null
    const act = l.reduce((s, f) => s + (LV.actualDevDays(f) as number), 0) / l.length
    const el = l.filter((f) => parseFloat(f.time) > 0)
    const est = el.length ? el.reduce((s, f) => s + parseFloat(f.time), 0) / el.length : null
    return { n: l.length, act: Math.round(act * 10) / 10, est: est != null ? Math.round(est * 10) / 10 : null }
  }

  const awaiting = allLive.filter((f) => !LV.liveHandled(f)).sort((a, b) => (b.completedOn || '').localeCompare(a.completedOn || ''))
  const done = allLive.filter(LV.liveHandled).sort((a, b) => (b.completedOn || '').localeCompare(a.completedOn || ''))

  const LiveCard: React.FC<{ f: Feature }> = ({ f }) => (
    <div className="cmp-card">
      <div className="cmp-card-top"><span className="cmp-card-t" onClick={() => openCardModal(f.id)}>{f.title}</span><span className="cmp-card-dev">{f.dev && DL[f.dev] ? DL[f.dev] : '—'}</span></div>
      <div className="cmp-card-meta"><span>Shipped {f.completedOn ? fmtDate(f.completedOn) : '—'}</span><span>{elapsedLabel(f.dateAdded, f.completedOn)} to live</span></div>
      <div className="cmp-card-acts">
        {f.noAnnounce ? (
          <>
            <span className="cmp-dismissed"><IcBan size={12} /> No announcement needed</span>
            <button className="cmp-btn ghost" onClick={() => mutate((fs) => LV.dismissAnnounce(fs, f.id))}>Undo</button>
          </>
        ) : (
          <>
            <button className={'cmp-btn' + (f.announcedOn ? ' on' : '')} onClick={() => mutate((fs) => LV.announceFeature(fs, f.id))}>{f.announcedOn ? <><IcCheck size={12} /> Announced to Requesters</> : <><IcMail size={12} /> Announce to Requesters</>}</button>
            <button className={'cmp-btn' + (f.publishedOn ? ' on' : '')} onClick={() => { if (f.publishedOn) { void unpublish(f) } else { setPublishFor(f) } }}>{f.publishedOn ? <><IcCheck size={12} /> Published to Updates Page</> : <><IcMegaphone size={12} /> Publish to Updates Page</>}</button>
            <button className={'cmp-btn' + (f.whatsappOn ? ' on' : '')} onClick={() => mutate((fs) => LV.whatsappAnnounce(fs, f.id))}>{f.whatsappOn ? <><IcCheck size={12} /> WhatsApp Groups</> : <><IcMessage size={12} /> Announce on WhatsApp Groups</>}</button>
            <button className="cmp-btn ghost" title="Bug fix or single-account custom work — skip the announcement" onClick={() => mutate((fs) => LV.dismissAnnounce(fs, f.id))}>Not needed</button>
          </>
        )}
      </div>
    </div>
  )

  const Panel: React.FC<{ k: string; title: string; count: number; children: React.ReactNode }> = ({ k, title, count, children }) => {
    const col = !!collapsed[k]
    return (
      <div className={'ovc cmp-panel' + (col ? ' collapsed' : '')}>
        <div className="ovc-hd cmp-hd" onClick={() => setCollapsed((c) => ({ ...c, [k]: !c[k] }))}>
          <h3><span className="cmp-caret">{col ? '▸' : '▾'}</span> {title}</h3><span className="ovpill grey">{count}</span>
        </div>
        {!col && <div className="ovc-body">{children}</div>}
      </div>
    )
  }

  return (
    <div id="completedView" style={{ padding: '16px 24px' }}>
      <div className="ovx">
        <div className="ovx-left">
          <div className="ovc">
            <div className="ovc-hd">
              <h3>Completed</h3>
              <div className="cmp-scope">
                {(['month', 'year', 'all'] as LV.LiveScope[]).map((v) => (
                  <button key={v} className={scope === v ? 'active' : ''} onClick={() => setScope(v)}>{v === 'month' ? 'This month' : v === 'year' ? 'This year' : 'All time'}</button>
                ))}
              </div>
            </div>
            <div className="ovc-body">
              <div className="cmp-stats">
                <div className="cmp-stat"><div className="cmp-stat-n">{scoped.length}</div><div className="cmp-stat-l">Shipped</div></div>
                <div className="cmp-stat"><div className="cmp-stat-n">{qw}</div><div className="cmp-stat-l">Quick wins</div></div>
                <div className="cmp-stat"><div className="cmp-stat-n">{onTimePct == null ? '—' : onTimePct + '%'}</div><div className="cmp-stat-l">On time</div></div>
                <div className="cmp-stat"><div className="cmp-stat-n">{annPct == null ? '—' : annPct + '%'}</div><div className="cmp-stat-l">Announced</div></div>
              </div>
              <div className="cmp-sec-h">By developer</div>
              {pie.total ? (
                <div className="cmp-pie-wrap">
                  <svg viewBox="0 0 120 120" width={118} height={118} className="ovx-pie">
                    {pie.single ? <circle cx={60} cy={60} r={54} fill={pie.color} /> : pie.slices.map((sl, i) => <path key={i} d={sl.d} fill={sl.color} />)}
                    <circle cx={60} cy={60} r={32} fill="#fff" />
                    <text x={60} y={59} textAnchor="middle" fontSize={22} fontWeight={700} fill="#111827">{pie.total}</text>
                    <text x={60} y={75} textAnchor="middle" fontSize={9} fill="#9ca3af">done</text>
                  </svg>
                  <div className="ovx-legend">
                    {legendKeys.map((k) => <div key={k} className="ovx-leg"><span className="ovx-sw" style={{ background: devColor(k) }} />{k === 'unassigned' ? 'Unassigned' : DL[k] || k}<span className="ovx-leg-n">{byDev[k]}</span></div>)}
                  </div>
                </div>
              ) : (
                <div className="cmp-empty">Nothing shipped in this period.</div>
              )}
              <div className="cmp-sec-h">Dev time: in development → live</div>
              {([['quickwin', 'Quick win'], ['low', 'Low'], ['medium', 'Med'], ['high', 'High']] as [string, string][]).map(([eff, lbl]) => {
                const s = effStat(eff)
                return (
                  <div key={eff} className="cmp-eff-row">
                    <span className="cmp-eff-l">{lbl} effort</span>
                    {s ? <><span className="cmp-eff-v">{s.act}d</span><span className="cmp-eff-est">{s.est != null ? 'est ' + s.est + 'd' : '—'}</span></> : <span className="cmp-eff-none">no data</span>}
                  </div>
                )
              })}
              <div className="cmp-eff-note">Avg working days from entering a dev column to going live, vs the estimate.</div>
            </div>
          </div>
        </div>
        <div className="ovx-right">
          <Panel k="awaiting" title="Awaiting announcement" count={awaiting.length}>
            {awaiting.length ? awaiting.map((f) => <LiveCard key={f.id} f={f} />) : <div className="cmp-empty">Nothing waiting — every shipped feature is announced, published or marked not needed.</div>}
          </Panel>
          <Panel k="announced" title="Announced & live" count={done.length}>
            {done.length ? done.map((f) => <LiveCard key={f.id} f={f} />) : <div className="cmp-empty">Handled features will appear here.</div>}
          </Panel>
        </div>
      </div>
      {publishFor && <PublishNoteModal feat={publishFor} onClose={() => setPublishFor(null)} />}
    </div>
  )
}
